import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import ReactSelect from "react-select";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  EyeOff,
  Package,
  Sparkles,
  SearchX,
  MapPin,
  X,
  Layers,
  CheckCircle2,
  FileEdit,
  ShoppingBag,
  AlertTriangle,
} from "lucide-react";
import {
  getMarketplaceProfile,
  getMyListings,
  deleteListing,
  unpublishListing,
  updateListing,
  MarketplaceListingListItem,
  ListingStatus,
  ListingType,
  getMarketplaceCategories,
  MarketplaceCategory,
  MarketplaceProfile,
} from "../../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import DeletePopup from "../../../atoms/Dashboard/Folder/DeletePopup";
import { usePermissions } from "../../../../hooks/usePermissions";
import PermissionGate from "../../../atoms/Utlis/PermissionGate";
import PublishFromEquipmentModal from "./PublishFromEquipmentModal";
import MarketplaceEmptyState, { MarketplaceLoadingSpinner } from "./MarketplaceEmptyState";
import { LISTING_TYPE_LABELS, LISTING_TYPE_COLORS, STATUS_COLORS } from "./marketplaceConstants";
import {
  MenuContainer,
  ThreeDotsButton,
  Dot,
  DropdownMenu,
  MenuItem,
  ItemIconContainer,
  ItemLabel,
  Backdrop,
  ToolbarLeft,
  ToolbarRight,
} from "../documentSharedStyles";
import {
  PageContainer,
  Hero,
  HeroGlow,
  HeroContent,
  HeroText,
  HeroBadge,
  PageTitle,
  PageSubtitle,
  Panel,
  Toolbar,
  SearchControl,
  ClearButton,
  EntriesControl,
  InlineSelect,
  FooterRow,
  FooterText,
  Pagination,
  PageButton,
  SecondaryButton,
  type MenuCoords,
} from "../quotationMasterListShared";
import { SubmitButton } from "../../../molecules/Dashboard/Member/memberFormStyles";
import {
  PanelTableScroll,
  TableWrap,
  PaymentsTable,
  AmountCell,
  ListHeader,
  ListTitle,
  CountBadge,
} from "../../../atoms/Dashboard/CustomerTransaction/transactionTableStyles";

const ACTION_BUTTONS = [
  { key: "edit", icon: Pencil, label: "Edit" },
  { key: "unpublish", icon: EyeOff, label: "Unpublish" },
  { key: "delete", icon: Trash2, label: "Delete" },
] as const;

const MarketplaceListingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canCrud = hasPermission("marketplace.crud");
  const [listings, setListings] = useState<MarketplaceListingListItem[]>([]);
  const [categories, setCategories] = useState<MarketplaceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [entries, setEntries] = useState(10);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<ListingType | "">("");
  const [statusFilter, setStatusFilter] = useState<ListingStatus | "">("");

  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [myProfile, setMyProfile] = useState<MarketplaceProfile | null>(null);

  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [menuCoords, setMenuCoords] = useState<MenuCoords | null>(null);
  const menuButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const scrollRef = useRef<HTMLDivElement>(null);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const res = await getMyListings({ page, page_size: entries });
      setListings(res?.results ?? []);
      setTotalCount(res?.count ?? 0);
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to load listings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchListings();
  }, [page, entries]);

  useEffect(() => {
    getMarketplaceProfile().then((res) => {
      if (res?.data) setMyProfile(res.data);
    }).catch(() => {});
    getMarketplaceCategories().then(setCategories).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return listings.filter((l) => {
      const matchSearch =
        !search ||
        l.title.toLowerCase().includes(search.toLowerCase()) ||
        (l.location || "").toLowerCase().includes(search.toLowerCase());
      const matchType = !typeFilter || l.listing_type === typeFilter;
      const matchStatus = !statusFilter || l.status === statusFilter;
      return matchSearch && matchType && matchStatus;
    });
  }, [listings, search, typeFilter, statusFilter]);

  const stats = useMemo(
    () => ({
      total: totalCount,
      active: listings.filter((l) => l.status === "active").length,
      draft: listings.filter((l) => l.status === "draft").length,
      saleRent: listings.filter((l) => l.listing_type === "sale" || l.listing_type === "rent").length,
    }),
    [listings, totalCount]
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / entries));
  const hasActiveFilters = !!(search || typeFilter || statusFilter);

  const closeMenu = useCallback(() => {
    setActiveMenuId(null);
    setMenuCoords(null);
  }, []);

  const handleToggleMenu = useCallback(
    (id: string) => {
      if (activeMenuId === id) {
        closeMenu();
        return;
      }

      const btn = menuButtonRefs.current[id];
      if (!btn) {
        setActiveMenuId(id);
        return;
      }

      const btnRect = btn.getBoundingClientRect();
      const estimatedMenuHeight = 180;
      const gap = 8;
      const spaceBelow = window.innerHeight - btnRect.bottom;
      const spaceAbove = btnRect.top;
      const openUp = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;
      const right = Math.max(8, window.innerWidth - btnRect.right);

      setMenuCoords(
        openUp
          ? { bottom: window.innerHeight - btnRect.top + gap, right }
          : { top: btnRect.bottom + gap, right }
      );
      setActiveMenuId(id);
    },
    [activeMenuId, closeMenu]
  );

  useEffect(() => {
    if (activeMenuId === null) return;
    const scrollEl = scrollRef.current;
    scrollEl?.addEventListener("scroll", closeMenu, { passive: true });
    window.addEventListener("resize", closeMenu);
    return () => {
      scrollEl?.removeEventListener("scroll", closeMenu);
      window.removeEventListener("resize", closeMenu);
    };
  }, [activeMenuId, closeMenu]);

  const activeMenuListing = useMemo(
    () => listings.find((l) => l.id === activeMenuId) ?? null,
    [listings, activeMenuId]
  );

  const handleDelete = async () => {
    if (!deleteTarget) return;
    if (myProfile?.is_active === false) {
      showErrorToast("Your marketplace account is frozen or deactivated. You cannot delete listings.");
      setDeleteTarget(null);
      return;
    }
    try {
      await deleteListing(deleteTarget.id);
      showSuccessToast("Listing deleted");
      setDeleteTarget(null);
      fetchListings();
    } catch (err: any) {
      showErrorToast(err?.response?.data?.message || "Failed to delete listing");
      setDeleteTarget(null);
    }
  };

  const handleUnpublish = async (id: string) => {
    if (myProfile?.is_active === false) {
      showErrorToast("Your marketplace account is frozen or deactivated. You cannot modify listings.");
      closeMenu();
      return;
    }
    try {
      await unpublishListing(id);
      showSuccessToast("Listing unpublished");
      fetchListings();
    } catch (err: any) {
      showErrorToast(err?.response?.data?.message || "Failed to unpublish listing");
    } finally {
      closeMenu();
    }
  };

  const handleStatusChange = async (id: string, newStatus: ListingStatus) => {
    if (myProfile?.is_active === false) {
      showErrorToast("Your marketplace account is frozen or deactivated. You cannot change listing status.");
      return;
    }
    try {
      await updateListing(id, { status: newStatus });
      showSuccessToast("Listing status updated successfully!");
      setListings((prev) => prev.map((l) => (l.id === id ? { ...l, status: newStatus } : l)));
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to update listing status");
    }
  };

  const clearFilters = () => {
    setSearch("");
    setTypeFilter("");
    setStatusFilter("");
  };

  return (
    <PageContainer>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroText>
            <HeroBadge>
              <Sparkles size={11} />
              Marketplace
            </HeroBadge>
            <PageTitle className="linear-text">My Listings</PageTitle>
            <PageSubtitle>
              Manage equipment for sale or rent, and photography service listings.
            </PageSubtitle>
          </HeroText>
          {canCrud && (
            <HeaderActions>
              <SecondaryButton
                type="button"
                onClick={() => setShowPublishModal(true)}
                style={{
                  width: "auto",
                  minWidth: 190,
                  height: 46,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  opacity: myProfile?.is_active === false ? 0.5 : 1,
                  cursor: myProfile?.is_active === false ? "not-allowed" : "pointer",
                }}
                disabled={myProfile?.is_active === false}
              >
                <Package size={16} /> Publish from Inventory
              </SecondaryButton>
              <SubmitButton
                type="button"
                $ready
                onClick={() => {
                  if (myProfile?.is_active === false) {
                    showErrorToast("Your marketplace account is frozen or deactivated.");
                    return;
                  }
                  navigate("/dashboard/marketplace/listings/new");
                }}
                style={{
                  minWidth: 168,
                  height: 46,
                  opacity: myProfile?.is_active === false ? 0.5 : 1,
                  cursor: myProfile?.is_active === false ? "not-allowed" : "pointer",
                }}
                disabled={myProfile?.is_active === false}
              >
                <Plus size={16} /> New Listing
              </SubmitButton>
            </HeaderActions>
          )}
        </HeroContent>
      </Hero>

      {myProfile?.is_active === false && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "14px 18px",
            marginBottom: "20px",
            borderRadius: "10px",
            background: "rgba(239, 68, 68, 0.12)",
            border: "1px solid rgba(239, 68, 68, 0.35)",
            color: "#fca5a5",
            fontSize: "14px",
            lineHeight: "1.5",
          }}
        >
          <AlertTriangle size={20} style={{ flexShrink: 0, color: "#ef4444" }} />
          <div>
            <strong>Account Frozen:</strong> Your marketplace account has been deactivated/frozen by an administrator.
            Your listings are currently inactive, and listing actions (create, edit, delete, publish) are disabled.
          </div>
        </div>
      )}

      <StatsGrid>
        <WalletHero>
          <WalletIconWrap>
            <Layers size={16} />
          </WalletIconWrap>
          <WalletHeroBody>
            <WalletHeroValue>{stats.total}</WalletHeroValue>
            <WalletHeroLabel>Total listings</WalletHeroLabel>
          </WalletHeroBody>
        </WalletHero>

        <StatCard $tone="#31a66a">
          <StatIcon $tone="#31a66a">
            <CheckCircle2 size={16} />
          </StatIcon>
          <StatBody>
            <StatValue>{stats.active}</StatValue>
            <StatLabel>Active on page</StatLabel>
          </StatBody>
        </StatCard>

        <StatCard $tone="#f09a3e">
          <StatIcon $tone="#f09a3e">
            <FileEdit size={16} />
          </StatIcon>
          <StatBody>
            <StatValue>{stats.draft}</StatValue>
            <StatLabel>Drafts on page</StatLabel>
          </StatBody>
        </StatCard>

        <StatCard $tone="#7c4dff">
          <StatIcon $tone="#7c4dff">
            <ShoppingBag size={16} />
          </StatIcon>
          <StatBody>
            <StatValue>{stats.saleRent}</StatValue>
            <StatLabel>Sale / Rent on page</StatLabel>
          </StatBody>
        </StatCard>
      </StatsGrid>

      <Panel>
        <Toolbar>
          <ToolbarLeft>
            <SearchControl>
              <Search size={16} />
              <input
                placeholder="Search by title or location..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <ClearButton type="button" onClick={() => setSearch("")}>
                  <X size={14} />
                </ClearButton>
              )}
            </SearchControl>

            <EntriesControl>
              <span>Show</span>
              <InlineSelect
                value={entries}
                onChange={(e) => {
                  setEntries(Number(e.target.value));
                  setPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </InlineSelect>
              <span>entries</span>
            </EntriesControl>
          </ToolbarLeft>

          <ToolbarRight>
            <FilterSelect
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as ListingType | "")}
            >
              <option value="">All Types</option>
              <option value="sale">For Sale</option>
              <option value="rent">For Rent</option>
              <option value="photographer">Photographer</option>
              <option value="videographer">Videographer</option>
            </FilterSelect>
            <FilterSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ListingStatus | "")}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="inactive">Inactive</option>
              <option value="sold">Sold</option>
            </FilterSelect>
          </ToolbarRight>
        </Toolbar>

        <ListHeader style={{ padding: "0 22px" }}>
          <ListTitle>Listing records</ListTitle>
          {!loading && (
            <CountBadge>
              {filtered.length} shown · {totalCount} total
            </CountBadge>
          )}
        </ListHeader>

        {loading ? (
          <MarketplaceLoadingSpinner padding="60px 22px" />
        ) : filtered.length === 0 ? (
          <MarketplaceEmptyState
            padding="32px 22px 48px"
            icon={
              hasActiveFilters ? (
                <SearchX size={36} strokeWidth={1.5} />
              ) : (
                <Package size={36} strokeWidth={1.5} />
              )
            }
            title={hasActiveFilters ? "No listings found" : "No listings yet"}
            description={
              hasActiveFilters
                ? "Nothing matched your search or filters. Try different keywords or broaden your criteria."
                : "Create your first listing to start selling, renting, or offering your photography services on the marketplace."
            }
            actions={[
              ...(hasActiveFilters
                ? [
                    {
                      label: "Clear filters",
                      onClick: clearFilters,
                      variant: "secondary" as const,
                    },
                  ]
                : []),
              ...(canCrud
                ? [
                    {
                      label: hasActiveFilters ? "Create New Listing" : "Create Your First Listing",
                      onClick: () => navigate("/dashboard/marketplace/listings/new"),
                      icon: <Plus size={15} />,
                    },
                    ...(!hasActiveFilters
                      ? [
                          {
                            label: "Publish from Inventory",
                            onClick: () => setShowPublishModal(true),
                            icon: <Package size={15} />,
                            variant: "secondary" as const,
                          },
                        ]
                      : []),
                  ]
                : []),
            ]}
            hint={
              !hasActiveFilters
                ? {
                    icon: <Sparkles size={13} />,
                    text: "Equipment for sale, rentals, photographers & videographers",
                  }
                : undefined
            }
          />
        ) : (
          <PanelTableScroll ref={scrollRef}>
            <TableWrap>
              <PaymentsTable $minWidth={760}>
                <thead>
                  <tr>
                    {["Listing", "Type", "Price", "Location", "Status", ...(canCrud ? ["Actions"] : [])].map((h) => (
                      <th key={h} style={h === "Actions" ? { textAlign: "right" } : undefined}>
                        <span>{h}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((listing) => (
                    <tr key={listing.id}>
                      <td>
                        <ListingInfo>
                          <ListingTitle>{listing.title}</ListingTitle>
                          <ListingMeta>{listing.seller_name}</ListingMeta>
                        </ListingInfo>
                      </td>
                      <td>
                        <TypeBadge $color={LISTING_TYPE_COLORS[listing.listing_type]}>
                          {LISTING_TYPE_LABELS[listing.listing_type]}
                        </TypeBadge>
                      </td>
                      <td>
                        <AmountCell>
                          ₹{Number(listing.price).toLocaleString("en-IN")}
                          <PriceUnit>/{listing.price_unit.replace("per_", "")}</PriceUnit>
                        </AmountCell>
                      </td>
                      <td>
                        {listing.location ? (
                          <LocationText>
                            <MapPin size={12} /> {listing.location}
                          </LocationText>
                        ) : (
                          <MutedText>—</MutedText>
                        )}
                      </td>
                      <td>
                        <StatusSelect
                          value={listing.status}
                          disabled={!canCrud}
                          $color={STATUS_COLORS[listing.status] || "#6b7280"}
                          onChange={(e) =>
                            handleStatusChange(listing.id, e.target.value as ListingStatus)
                          }
                        >
                          <option value="draft">draft</option>
                          <option value="active">active</option>
                          <option value="inactive">inactive</option>
                          {listing.listing_type === "sale" && <option value="sold">sold</option>}
                        </StatusSelect>
                      </td>
                      {canCrud && (
                        <td style={{ textAlign: "right" }}>
                          <MenuContainer>
                            <ThreeDotsButton
                              ref={(el) => {
                                menuButtonRefs.current[listing.id] = el;
                              }}
                              type="button"
                              aria-expanded={activeMenuId === listing.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleMenu(listing.id);
                              }}
                            >
                              <Dot />
                              <Dot />
                              <Dot />
                            </ThreeDotsButton>
                          </MenuContainer>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </PaymentsTable>
            </TableWrap>
          </PanelTableScroll>
        )}

        {!loading && filtered.length > 0 && <PanelDivider />}

        <FooterRow>
          <FooterText>
            {totalCount > 0
              ? `Showing ${(page - 1) * entries + 1} to ${Math.min(page * entries, totalCount)} of ${totalCount} entries`
              : "No listings"}
          </FooterText>
          <Pagination>
            <PageButton disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </PageButton>
            {Array.from({ length: totalPages }, (_, i) => (
              <PageButton key={i} $active={page === i + 1} onClick={() => setPage(i + 1)}>
                {i + 1}
              </PageButton>
            ))}
            <PageButton disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              Next
            </PageButton>
          </Pagination>
        </FooterRow>
      </Panel>

      {activeMenuListing && menuCoords &&
        createPortal(
          <>
            <Backdrop onClick={closeMenu} />
            <DropdownMenu
              $fixed
              style={{
                top: menuCoords.top,
                bottom: menuCoords.bottom,
                right: menuCoords.right,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {ACTION_BUTTONS.map((btn) => {
                if (btn.key === "unpublish" && activeMenuListing.status !== "active") {
                  return null;
                }
                const Icon = btn.icon;
                return (
                  <MenuItem
                    key={btn.key}
                    type="button"
                    onClick={() => {
                      closeMenu();
                      if (btn.key === "edit") {
                        navigate(`/dashboard/marketplace/listings/edit/${activeMenuListing.id}`);
                      } else if (btn.key === "unpublish") {
                        handleUnpublish(activeMenuListing.id);
                      } else if (btn.key === "delete") {
                        setDeleteTarget({
                          id: activeMenuListing.id,
                          title: activeMenuListing.title,
                        });
                      }
                    }}
                  >
                    <ItemIconContainer>
                      <Icon size={14} />
                    </ItemIconContainer>
                    <ItemLabel>{btn.label}</ItemLabel>
                  </MenuItem>
                );
              })}
            </DropdownMenu>
          </>,
          document.body
        )}

      {deleteTarget && (
        <DeletePopup
          text={`Delete "${deleteTarget.title}"? This action cannot be undone.`}
          buttonText="Delete Listing"
          cancel={() => setDeleteTarget(null)}
          Delete={handleDelete}
        />
      )}

      {showPublishModal && (
        <PublishFromEquipmentModal
          onClose={() => setShowPublishModal(false)}
          onSuccess={() => {
            setShowPublishModal(false);
            fetchListings();
          }}
        />
      )}
    </PageContainer>
  );
};

export default MarketplaceListingsPage;

const gradient =
  "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const WalletHero = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  color: #fff;
  background: ${gradient};
  box-shadow: 0 8px 22px rgba(201, 77, 170, 0.22);
  text-align: left;

  &::after {
    content: "";
    position: absolute;
    right: -20px;
    bottom: -24px;
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const WalletIconWrap = styled.div`
  position: relative;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.22);
`;

const WalletHeroBody = styled.div`
  position: relative;
  z-index: 1;
  min-width: 0;
`;

const WalletHeroValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  line-height: 1.1;
`;

const WalletHeroLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
  margin-top: 2px;
`;

const StatCard = styled.div<{ $tone: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 6px 18px rgba(23, 23, 23, 0.04);
`;

const StatIcon = styled.div<{ $tone: string }>`
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => p.$tone};
  background: ${(p) => `${p.$tone}18`};
`;

const StatBody = styled.div`
  min-width: 0;
`;

const StatValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #171717;
  line-height: 1.1;
`;

const StatLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
  margin-top: 2px;
`;

const filterSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: "46px",
    minWidth: "140px",
    borderRadius: "12px",
    borderColor: state.isFocused ? "rgba(201, 77, 170, 0.35)" : "rgba(23, 23, 23, 0.1)",
    background: state.isFocused ? "#ffffff" : "#fafafa",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(201, 77, 170, 0.08)" : "none",
    fontFamily: "Urbanist, sans-serif",
    fontSize: "13px",
    fontWeight: 600,
    color: "#171717",
    cursor: "pointer",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease",
    "&:hover": {
      borderColor: "rgba(201, 77, 170, 0.28)",
      background: "#ffffff",
    },
  }),
  valueContainer: (base: any) => ({ ...base, padding: "0 14px" }),
  input: (base: any) => ({ ...base, margin: 0, padding: 0 }),
  placeholder: (base: any) => ({ ...base, color: "rgba(23, 23, 23, 0.4)", fontWeight: 500 }),
  singleValue: (base: any) => ({ ...base, color: "#171717" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base: any, state: any) => ({
    ...base,
    color: state.isFocused ? "#c94daa" : "rgba(23, 23, 23, 0.45)",
    padding: "0 12px 0 8px",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 0.15s ease, color 0.15s ease",
  }),
  menu: (base: any) => ({
    ...base,
    marginTop: "8px",
    borderRadius: "16px",
    padding: "6px",
    border: "1px solid rgba(201, 77, 170, 0.12)",
    boxShadow: "0 18px 36px rgba(23, 23, 23, 0.14)",
    overflow: "hidden",
    zIndex: 9999,
  }),
  menuList: (base: any) => ({ ...base, padding: 0 }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  option: (base: any, state: any) => ({
    ...base,
    borderRadius: "12px",
    padding: "11px 13px",
    fontFamily: "Urbanist, sans-serif",
    fontSize: "14px",
    fontWeight: state.isSelected ? 700 : 600,
    color: "#171717",
    background: state.isSelected
      ? "linear-gradient(135deg, rgba(255, 201, 81, 0.18), rgba(201, 77, 170, 0.16))"
      : state.isFocused
        ? "rgba(201, 77, 170, 0.08)"
        : "#ffffff",
  }),
};

type BasicSelectProps = {
  value: string;
  onChange: (e: { target: { value: string } }) => void;
  children: React.ReactNode;
  disabled?: boolean;
};

const FilterSelect: React.FC<BasicSelectProps> = ({ value, onChange, children, disabled }) => {
  const options = React.Children.toArray(children)
    .filter((child): child is React.ReactElement<any> => React.isValidElement<any>(child) && child.type === "option")
    .map((child) => ({ value: String(child.props.value), label: child.props.children }));

  const selectedOption = options.find((option) => option.value === String(value)) ?? null;

  return (
    <ReactSelect
      value={selectedOption}
      onChange={(option) => onChange({ target: { value: option?.value ?? "" } })}
      options={options}
      styles={filterSelectStyles}
      menuPosition="fixed"
      menuPortalTarget={document.body}
      isSearchable={false}
      isDisabled={disabled}
    />
  );
};

const PanelDivider = styled.div`
  height: 1px;
  margin: 0 22px;
  background: rgba(23, 23, 23, 0.06);
`;

const ListingInfo = styled.div``;

const ListingTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #171717;
  margin-bottom: 3px;
`;

const ListingMeta = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  color: rgba(23, 23, 23, 0.5);
`;

const TypeBadge = styled.span<{ $color: string }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 100px;
  font-size: 11px;
  font-weight: 700;
  font-family: Montserrat, sans-serif;
  background: ${(p) => p.$color}18;
  color: ${(p) => p.$color};
`;

const getStatusSelectStyles = (color: string) => ({
  control: (base: any) => ({
    ...base,
    minHeight: "30px",
    borderRadius: "999px",
    border: `1px dashed ${color}50`,
    background: `${color}18`,
    boxShadow: "none",
    minWidth: "108px",
    cursor: "pointer",
    "&:hover": {
      background: `${color}25`,
      border: `1px solid ${color}70`,
    },
  }),
  valueContainer: (base: any) => ({ ...base, padding: "0 8px 0 10px" }),
  input: (base: any) => ({ ...base, margin: 0, padding: 0 }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base: any, state: any) => ({
    ...base,
    color,
    padding: "0 8px 0 4px",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 0.15s ease",
  }),
  singleValue: (base: any) => ({
    ...base,
    color,
    fontFamily: "Montserrat, sans-serif",
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "capitalize",
  }),
  menu: (base: any) => ({
    ...base,
    marginTop: "8px",
    borderRadius: "14px",
    padding: "6px",
    border: "1px solid rgba(201, 77, 170, 0.12)",
    boxShadow: "0 16px 32px rgba(23, 23, 23, 0.14)",
    overflow: "hidden",
    zIndex: 9999,
  }),
  menuList: (base: any) => ({ ...base, padding: 0 }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  option: (base: any, state: any) => ({
    ...base,
    borderRadius: "10px",
    padding: "10px 12px",
    fontFamily: "Montserrat, sans-serif",
    fontSize: "12px",
    fontWeight: state.isSelected ? 700 : 600,
    textTransform: "capitalize",
    color: "#171717",
    background: state.isSelected
      ? "linear-gradient(135deg, rgba(255, 201, 81, 0.18), rgba(201, 77, 170, 0.16))"
      : state.isFocused
        ? "rgba(201, 77, 170, 0.08)"
        : "#ffffff",
  }),
});

const StatusSelect: React.FC<BasicSelectProps & { $color: string }> = ({ value, onChange, children, $color, disabled }) => {
  const options = React.Children.toArray(children)
    .filter((child): child is React.ReactElement<any> => React.isValidElement<any>(child) && child.type === "option")
    .map((child) => ({ value: String(child.props.value), label: child.props.children }));

  const selectedOption = options.find((option) => option.value === String(value)) ?? null;

  return (
    <ReactSelect
      value={selectedOption}
      onChange={(option) => onChange({ target: { value: option?.value ?? "" } })}
      options={options}
      styles={getStatusSelectStyles($color)}
      menuPosition="fixed"
      menuPortalTarget={document.body}
      isSearchable={false}
      isDisabled={disabled}
    />
  );
};

const PriceUnit = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.5);
  margin-left: 2px;
`;

const LocationText = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: rgba(23, 23, 23, 0.6);
`;

const MutedText = styled.span`
  color: rgba(23, 23, 23, 0.3);
  font-size: 13px;
`;
